import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils/className';

const alertVariants = cva(
  'relative w-full rounded-lg border p-4 [&>svg~*]:pl-2 [&>svg+div]:translate-y-[-3px] [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground',
  {
    variants: {
      variant: {
        default: 'bg-background text-foreground',
        destructive:
          'bg-transparent border-border-action-negative-idle text-text-action-negative-idle',
        primary: 'bg-purple-700 border-purple-500 text-white',
        info: 'bg-fill-bg-emphasis border-border-interactive-emphasis-idle',
        notification:
          'bg-transparent text-text-general-neutral-mid rounded-lg border border-border-panel-emphasis',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const POSITION_CLASSNAMES = {
  'top-right': 'fixed right-4',
  'top-left': 'fixed left-4',
  'bottom-right': 'fixed bottom-4 right-4',
  'bottom-left': 'fixed bottom-4 left-4',
} as const;

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
));

Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn(
      'flex gap-4 font-medium leading-none tracking-tight text-inherit',
      className,
    )}
    {...props}
  />
));

AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-sm [&_p]:leading-relaxed', className)}
    {...props}
  />
));

AlertDescription.displayName = 'AlertDescription';

const AlertContainer = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
    topOffset?: number;
  }
>(
  (
    { className, position = 'top-right', topOffset = 16, children, ...props },
    ref,
  ) => {
    const isTopPosition = position === 'top-right' || position === 'top-left';
    const style = isTopPosition ? { top: `${topOffset}px` } : undefined;

    return (
      <div
        ref={ref}
        className={cn(
          POSITION_CLASSNAMES[position],
          'z-1 flex flex-col gap-3 pointer-events-none [&>*]:pointer-events-auto [&>*]:w-fit [&>*]:ml-auto',
          className,
        )}
        style={style}
        {...props}
      >
        {children}
      </div>
    );
  },
);

AlertContainer.displayName = 'AlertContainer';

export { Alert, AlertTitle, AlertDescription, AlertContainer };
